/*
 * Crud - 通用增删改查表格组件
 *
 * 本组件是整个系统的核心数据展示组件，封装了以下功能：
 *   1. 数据表格展示 (分页、排序、行选择、斑马纹)
 *   2. 筛选面板 (按列定义自动生成筛选表单，支持展开/收起)
 *   3. 新建/编辑弹窗 (根据列定义自动生成表单，支持校验)
 *   4. 导出功能 (调用 /{entityKey}/export 导出当前筛选数据)
 *   5. 自定义行操作按钮 (通过 getRowActions 注入)
 *   6. 自定义全局操作按钮 (通过 actions 注入)
 *
 * === entityKey 与 API 映射关系 ===
 *
 * entityKey 是本组件的核心概念，它决定了所有 API 调用的路径：
 *   - 列表查询:   GET    /{entityKey}?page=&size=&orderBy=&...筛选参数
 *   - 新建记录:   POST   /{entityKey}             body = 表单值
 *   - 编辑记录:   PUT    /{entityKey}             body = 表单值 + id
 *   - 导出数据:   GET    /{entityKey}/export?...筛选参数
 *   - 图片上传:   POST   {apiClient.getUploadUrl()}  (IMAGE类型列专用)
 *
 * 示例:
 *   entityKey="mortgageLoan/leadList"       → GET /mortgageLoan/leadList
 *   entityKey="repaymentPlan/listAll"       → GET /repaymentPlan/listAll
 *   entityKey="mortgageLoan"                → POST/PUT /mortgageLoan
 *
 * === 后端 Controller 对接模式 ===
 *
 * 列表接口需返回统一的分页结构:
 *   { code: "ok", data: { current: 1, total: 100, size: 50, records: [...] } }
 *
 * 新建/编辑接口需返回:
 *   { code: "ok", ... }
 *
 * === 使用示例 ===
 *
 * <Crud
 *     name="还款计划"
 *     entityKey="repaymentPlan/listAll"
 *     pageSize={50}
 *     cols={columns}
 *     canCreate={true}
 *     canUpdate={true}
 *     canDelete={true}
 *     params={{loanId: 123}}                    // 额外固定查询参数
 *     getRowActions={(row) => [myAction]}        // 行操作按钮
 *     getCrudApi={(api) => setMyApi(api)}        // 获取 CrudApi 引用
 * />
 *
 * === 子组件依赖 ===
 *   无直接子组件，但被几乎所有业务页面使用（RepaymentPlan、MortgageLead、MortgageLoan 等）
 */

import React, {ReactElement, ReactNode, useEffect, useState} from "react";
import {
    Button,
    DatePicker,
    Form,
    Modal,
    Table,
    Tag,
    TagInput,
    Toast,
    TreeSelect,
    withField,
    Divider,
    Checkbox,
    Collapse,
} from "@douyinfe/semi-ui";
import {ColumnProps} from "@douyinfe/semi-ui/lib/es/table";
import {ApiClient, ApiResp, Page} from "../../api/ApiClient";
import Title from "@douyinfe/semi-ui/lib/es/typography/title";
import "./index.scss";
import {
    IconPlus,
    IconDownloadStroked,
    IconSidebar,
    IconChevronDown,
    IconEyeClosed,
    IconMinus,
    IconUndo,
    IconSearch,
    IconEyeOpened, IconModalStroked
} from "@douyinfe/semi-icons";
import {FormApi} from "@douyinfe/semi-ui/lib/es/form";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
    formatPercent,
    formatDate,
    formatDatetimeStr,
    setObjValue,
    formatDateTime,
    formatFloat, hasValidChildren,
} from "../../utils";
import {OptionProps} from "@douyinfe/semi-ui/lib/es/tree";

import pubsub from "pubsub-js";

const Select = Form.Select;
const TreeSelectInput = withField(TreeSelect);
const FormDatePicker = withField(DatePicker);

// 行操作/全局操作的按钮定义
export interface CrudAction {
    name: string;
    loading?: boolean;
    icon?: ReactNode;
    type?: string;
    action: (a: any, all: any[] | undefined) => void;
}

export interface RowAction {
    name: string;
    loading?: boolean;
    icon?: ReactNode;
    action: (a: any, all: any) => void;
}

// 列类型枚举，决定了列在表格中的渲染方式和在表单中的输入控件类型
export enum ColType {
    NUMBER,        // 数字 → 表格显示数值, 表单用 InputNumber
    STRING,        // 字符串 → 表格显示文本, 表单用 Input
    TEXT,          // 长文本 → 表格显示文本, 表单用 TextArea
    SELECT,        // 单选 → 表格显示选项label, 表单用 Select (需配合 getOptions)
    MULTI_SELECT,  // 多选 → 表格显示选项label, 表单用 Select multiple
    DATETIME,      // 日期时间 → 表格显示日期时间, 表单用 DatePicker type=dateTime
    DATE,          // 日期 → 表格显示日期, 表单用 DatePicker type=date
    IMAGE,         // 图片 → 表格显示缩略图, 表单用 Upload
    BOOL,          // 布尔 → 表格显示"是/否" Tag, 表单用 Input
    AMOUNT,        // 金额 → 表格显示(值/100), 表单用 InputNumber (保存时×100)
    PERCENTAGE,    // 百分比 → 表格显示百分比格式, 表单用 InputNumber (保存时×100)
    RICH_TEXT,     // 富文本 → 表格显示原文, 表单用 ReactQuill 富文本编辑器
    DATE_RANGE,    // 日期范围 → 仅用于筛选条件, 筛选时转为 "startDate,endDate" 格式
    TAG,           // 标签输入 → 表单用 TagInput
    TREE,          // 树形单选 → 表单用 TreeSelect
    TREE_MUL,      // 树形多选 → 表单用 TreeSelect multiple
    CHECK_BOX      // 复选框 → 表单用 Checkbox
}

// 下拉选项数据结构(用于 SELECT/TREE 等类型列)
export interface CrudOption {
    value: string | number;
    label: string;
    labelExt?: string;
    key: string;
    children?: CrudOption[];
    disabled?: boolean;
}

// 快速创建选项的辅助函数
export function optionOf(value: string, label: string): CrudOption {
    return {
        value,
        label,
        key: value,
    };
}

// 根据 value 查找对应的 label
export function getLabelForValue(value: string, options: CrudOption[]) {
    let o = options.filter((o) => o.value == value)[0];
    return o ? o.label : value;
}

// 列定义接口 - 每个列定义了在表格、表单、筛选中的行为
export interface Col {
    name: string;           // 列显示名称(表头文字、表单标签)
    key: string;            // 字段名(对应后端DTO字段名)
    type: ColType;          // 列类型(决定渲染方式和表单控件)
    width?: number;         // 列宽度
    colSort?: number;       // 列排序顺序(未使用)
    getType?: (row: any | null | undefined) => ColType;  // 动态列类型(根据行数据决定类型)
    showInFilter?: boolean;     // 是否显示在筛选面板中(默认 false)
    showInPlusFilter?: boolean; // 是否显示在"展开"筛选面板中(需点击展开才显示)
    colFilterSort?: number;     // 筛选面板中的排序顺序
    hideInEdit?: boolean;       // 编辑弹窗中隐藏该字段
    hideInCreate?: boolean;     // 新建弹窗中隐藏该字段
    hideInList?: boolean;       // 表格中隐藏该列(仅用于筛选或表单)
    disabled?: boolean;         // 表单中该字段是否禁用
    hidden?: boolean;           // 表单中该字段是否完全隐藏(display:none)
    placeHolder?: string;       // 表单输入框的占位文字
    helpText?: string;          // 表单字段的帮助提示文字
    render?: (value: any, row: any, index: number) => string | ReactNode;  // 自定义表格列渲染函数
    getOptions?: (row: any | null | undefined) => Promise<CrudOption[]>;   // 获取下拉选项(异步)
    imageLimit?: number;        // 图片上传数量限制(IMAGE类型, 默认1)
    optional?: boolean;         // 表单中该字段是否可选(默认必填)
    defaultSortOrder?: "ascend" | "descend" | boolean;  // 默认排序方向(设置后表格该列可排序)
    onChange?: (e: any | null | undefined) => void;      // 表单值变化回调
    textRows?: number;          // TextArea行数(TEXT类型, 默认6)
}

// Crud 组件属性接口
export interface CrudProps {
    desc?: ReactElement;                // 表格上方的自定义描述区域
    hideTitle?: boolean;                // 是否隐藏标题栏
    name: string;                       // 页面名称(显示为标题和弹窗标题)
    entityKey: string;                  // API路径(核心属性, 决定 GET/POST/PUT 的URL)
    pageSize: number;                   // 每页条数
    cols: Col[];                        // 列定义数组
    canCreate?: boolean;                // 是否显示"新建"按钮
    canUpdate?: boolean;                // 是否显示行"编辑"按钮
    canDelete?: boolean;                // 是否显示行"删除"按钮(目前未在此组件实现)
    canExport?: boolean;                // 是否显示"导出"按钮
    actions?: CrudAction[];             // 自定义全局操作按钮(显示在顶部操作栏)
    getRowActions?: (a: any) => CrudAction[];  // 根据行数据返回自定义行操作按钮
    getCrudApi?: (crudApi: CrudApi) => void;   // 获取 CrudApi 引用(供父组件调用 refresh/getData 等)
    fetchDataAfterMethod?: (data: any) => void; // 数据加载完成后的回调(传入查询参数)
    getFilterFormApi?: (crudApi: CrudApi, filterFormApi: FormApi) => void;  // 获取筛选表单引用
    params?: Record<string, any>;       // 额外的固定查询参数(每次请求都会带上)
    initParams?: Record<string, any>;   // 初始化时的查询参数(仅首次加载使用)
    select?: boolean;                   // 是否启用行选择(复选框)
    rowKey?: string;                    // 行数据的唯一标识字段名(默认 "id")
    initFilterValues?: any;             // 筛选表单的初始值
    bordered?: boolean;                 // 表格是否显示边框
    rowActionWidth?: number;            // 操作列宽度(0则不显示操作列)
    treeTable?: boolean;
}

// CrudApi - 暴露给父组件的操作接口
export interface CrudApi {
    refresh: () => void;                // 刷新当前页数据(重新请求后端)
    getData: () => any[];               // 获取当前页数据(records数组)
    getQueryParams: () => any;          // 获取当前查询参数(含筛选条件和分页)
    executeQuery: (data: any) => void;  // 执行自定义查询(重置到第一页)
    getFilterFormApi: () => any;        // 获取筛选表单的 FormApi 引用
}

// 列对齐方式(当前统一左对齐)
function getColumnAlign(col: Col) {
    return "left";
}

// 将 Col 列定义转换为 Semi Table 的 ColumnProps
// 根据 ColType 设置默认的 render 函数:
//   IMAGE → 显示缩略图背景
//   BOOL  → 显示"是/否" Tag
//   AMOUNT → 值/100 (分→元)
//   PERCENTAGE → 百分比格式
//   NUMBER → 空值显示0
function toColumn(col: Col): ColumnProps {
    let render = col.render;
    if (render === undefined) {
        if (col.type == ColType.IMAGE) {
            render = (r) => {
                return (
                    <div
                        className="crud-image"
                        style={{backgroundImage: `url(${r})`}}
                    />
                );
            };
        }
        if (col.type == ColType.BOOL) {
            render = (r) =>
                r ? <Tag color="green">是</Tag> : <Tag color="red">否</Tag>;
        }
        if (col.type == ColType.AMOUNT) {
            render = (r) => (r ? r / 100.0 : 0);
        }
        if (col.type == ColType.PERCENTAGE) {
            render = (r) => (r ? formatPercent(r) : "0.00%");
        }
        if (col.type == ColType.NUMBER) {
            render = (r) => (r ? r : 0);
        }
    }

    return {
        align: "left",
        title: col.name,
        dataIndex: col.key,
        width: col.width,
        render: render,
        defaultSortOrder: col.defaultSortOrder,
        sorter: col.defaultSortOrder === undefined ? undefined : true,
    };
}

const apiClient = ApiClient.getDefault();

// 弹窗模式: 编辑(ModalMode.EDIT) 或 新建(ModalMode.CREATE)
enum ModalMode {
    EDIT,
    CREATE,
}

// 支持嵌套keypath的对象取值, 如 getObjValue(obj, "a.b.c") → obj.a.b.c
function getObjValue(obj: any, keypath: string): any {
    let paths = keypath.split(".");
    for (let i = 0; i < paths.length; i++) {
        obj = obj[paths[i]];
    }
    return obj;
}

const RichTextEditor = withField(ReactQuill);
const TagInputField = withField(TagInput);

export function Crud<T>(props: CrudProps) {
    // 从 cols 中获取默认排序配置, 转为 "字段名,排序方向" 格式
    const getPropsSortOrder = () => {
        const col = props.cols.filter((c) => c.defaultSortOrder)[0];
        return col
            ? {
                orderBy: col.key + "," + col.defaultSortOrder,
            }
            : {};
    };

    // 分页状态
    let crudActions: CrudAction[] = [];
    let [currentPage, setCurrentPage] = useState(1);
    let [currentPageSize, setCurrentPageSize] = useState(50);

    // 新建/编辑弹窗状态
    let [modalVisible, setModalVisible] = useState(false);
    let [editingDto, setEditingDto] = useState<any>();       // 正在编辑的数据对象(null=新建模式)
    let [modalLoading, setModalLoading] = useState(false);

    // 下拉选项缓存: { [colKey]: CrudOption[] }
    let [options, setOptions] = useState({} as Record<string, CrudOption[]>);

    // 表单引用
    let [entityFormApi, setEntityFormApi] = useState<FormApi>();   // 新建/编辑弹窗的表单
    let [filterFormApi, setFilterFormApi] = useState<FormApi>();   // 筛选面板的表单

    // 行选择状态
    let [selectedRows, setSelectedRows] = useState<any[]>();

    // 导出loading状态
    let [exporting, setExporting] = useState(false);

    // 筛选面板显示状态(持久化到 localStorage)
    let [showFilter, setShowFilter] = useState(
        apiClient.getPref<boolean>("filter")
    );
    //
    let [showExpandRow, setShowExpandRow] = useState(false);

    // 筛选值(变化时触发重新查询)
    let [filterValues, setFilterValues] = useState<any>(undefined);

    // 刷新标志(refreshing=true 时触发 useEffect 重新加载数据)
    let [refreshing, setRefreshing] = useState(false);

    // 排序参数
    let [sortOrder, setSortOrder] = useState(getPropsSortOrder());

    // 行选择配置
    let [rowSelection, setRowSelection] = useState<any>();

    // 表格可调整列宽(当前未启用)
    let [resizable, setResizable] = useState(false);

    // 表格高度(根据窗口高度动态计算)
    let [tableHeight, setTableHeight] = useState(6000);

    // 当前查询参数(供导出和父组件使用)
    let [queryParams, setQueryParams] = useState<any>(undefined);

    // 展开/收起筛选面板的显示状态
    let [showPlusFilter, setShowPlusFilter] = useState("");
    let [showMinusFilter, setShowMinusFilter] = useState("none");

    // 表格滚动配置(支持 sticky 模式)
    let [scroll, setScroll] = useState({} as any);

    // 已选中的行 key 数组
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

    // 分页数据
    let [page, setPage] = useState<Page<any>>({
        current: 1,
        total: 0,
        size: props.pageSize,
        records: [],
    });

    // CrudApi 实例 - 暴露给父组件的操作接口
    let crudApiV: CrudApi = {
        refresh: () => {
            setRefreshing(true);   // 触发 refreshing 变化 → useEffect 重新调用 fetchData
        },
        getData: () => {
            return page.records;
        },
        getQueryParams: () => {
            return queryParams;
        },
        executeQuery: (data: any) => {
            // 自定义查询，重置到第一页
            fetchData(1, currentPageSize, data);
        },
        getFilterFormApi: () => {
            return filterFormApi;
        },
    };

    /**
     * 核心数据加载函数
     * 调用 GET /{entityKey} 加载分页数据
     *
     * 请求参数合并优先级(后者覆盖前者):
     *   page, size → sortOrder → urlParams(自定义参数) → props.params(固定参数) → filterValues(筛选值)
     *
     * @param currentPage   当前页码
     * @param currentPageSize 每页条数
     * @param urlParams     额外的URL参数(如排序条件)
     */
    let fetchData = async (
        currentPage: number,
        currentPageSize: number,
        urlParams?: any
    ) => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
        setLoading(true);
        setCurrentPage(currentPage);
        setCurrentPageSize(currentPageSize);
        initOptions();  // 预加载所有下拉选项

        // 合并查询参数: 分页 + 排序 + 自定义参数 + 固定参数 + 筛选值
        let params = {
            page: currentPage.toString(),
            size: currentPageSize.toString(),
            ...sortOrder,
            ...urlParams,
            ...props.params,
            ...formatFilterValues(filterFormApi?.getValues()),
        };
        setQueryParams(params);
        try {
            apiClient
                .request("/" + props.entityKey, "GET", params)
                .then((apiResp: any) => {
                    setPage(apiResp.data);

                    // 更新 crudApiV 中的 getData 返回最新数据
                    crudApiV = {
                        refresh: () => {
                            setRefreshing(true);
                        },
                        getData: () => {
                            return apiResp.data.records;
                        },
                        getQueryParams: () => {
                            return params;
                        },
                        executeQuery: (data: any) => {
                            fetchData(1, currentPageSize, data);
                        },
                        getFilterFormApi: () => {
                            return filterFormApi;
                        },
                    };
                    // 回传 CrudApi 给父组件
                    if (props.getCrudApi) {
                        props.getCrudApi(crudApiV);
                    }
                    // 数据加载完成后回调
                    if (props.fetchDataAfterMethod) {
                        props.fetchDataAfterMethod(params);
                    }

                    setLoading(false);

                }).catch(e => {
                setLoading(false);
            });
        } catch (e) {
            setLoading(false);
        } finally {
            //为了重新渲染组件
            //setResizable(resizable ? false : true);
        }
    };

    let [loading, setLoading] = useState(false);

    // 过滤掉 hideInList=true 的列，转换为 Semi Table 的 ColumnProps
    let columns = props.cols.filter((c) => !c.hideInList).map(toColumn);

    // 组件挂载时回传 CrudApi 给父组件
    useEffect(() => {
        if (props.getCrudApi) {
            props.getCrudApi(crudApiV);
        }
    }, []);

    /**
     * 获取行操作按钮列表
     * 默认操作: 如果 canUpdate=true 则添加"编辑"按钮
     * 自定义操作: 通过 getRowActions 注入
     */
    let getRowActions = (entity: T): CrudAction[] => {
        let actions: CrudAction[] = [];
        // 默认的编辑按钮: 设置 editingDto → 打开弹窗
        if (props.canUpdate) {
            actions.push({
                name: "编辑",
                action: async () => {
                    setEditingDto(entity);          // 设置编辑对象(非null=编辑模式)
                    await initOptions(entity);       // 加载下拉选项(传入行数据用于动态选项)
                    setModalVisible(true);
                    return Promise.resolve();
                },
            });
        }
        // 添加自定义行操作(如"删除"、"修改计划"、"取消还款"等)
        if (props.getRowActions) {
            actions = actions.concat(props.getRowActions(entity));
        }
        return actions;
    };

    // 添加操作列(最右侧固定列)
    if (props.rowActionWidth == undefined || props.rowActionWidth > 0) {
        columns.push({
            title: "操作",
            dataIndex: "operation",
            width: props.rowActionWidth || props.rowActionWidth == 0 ? props.rowActionWidth : 140,
            fixed: "right",
            render: (text, entity: T) => {
                // 树形数据中的子节点(如汇总行)不显示操作按钮
                if (hasValidChildren(entity)) {
                    return (<div></div>)
                }
                return (
                    <div className="crud-row-actions" >
                        {getRowActions(entity).map((a, i) => (
                            <Button
                                style={{margin:'3px'}}
                                key={a.name + i}
                                theme="outline"
                                size="small"
                                type={convertButtonType(a.type)}
                                icon={a.icon}
                                onClick={() => a.action(entity, page.records)}
                            >
                                {a.name}
                            </Button>
                        ))}
                    </div>
                )
            },
        });
    }

    let convertButtonType = (type: string):any => {
        return type?type:'primary';
    };

    // 分页变化处理(当前由 handleChange 统一处理排序和分页)
    let handlePageChange = (page: number) => {
        //表头排序、筛选、分页会触发onChange，使用了onChange，就不需要使用onPageChange
    };

    // 新建按钮处理: 清空编辑对象 → 加载选项 → 打开弹窗
    let handleCreate = async () => {
        await initOptions();
        setModalVisible(true);
    };

    /**
     * 导出按钮处理
     * 调用 GET /{entityKey}/export 使用当前查询参数
     * 后端返回下载URL，新窗口打开
     */
    let handleExport = async () => {
        setExporting(true);
        try {
            const resp = await apiClient.request<string>(
                "/" + props.entityKey + "/export",
                "GET",
                queryParams
            );
            window.open(resp.data, "_blank");
            setExporting(false);
        } catch (e) {
            setExporting(false);
        }
    };

    // 构建顶部操作按钮列表
    if (props.canCreate) {
        crudActions.push({
            name: "新建",
            action: handleCreate,
            icon: <IconPlus/>,
        });
    }
    if (props.canExport) {
        crudActions.push({
            name: "导出",
            action: handleExport,
            loading: exporting,
            icon: <IconDownloadStroked/>,
        });
    }
    if (props.actions) {
        crudActions = crudActions.concat(props.actions);
    }

    // 切换筛选面板显示/隐藏(状态持久化到 localStorage)
    const handleToggleFilter = () => {
        const result = !showFilter;
        setShowFilter(result);
        apiClient.putPref("filter", result);
    };

    // 渲染顶部操作栏: 筛选切换按钮 + 新建/导出/自定义按钮
    let actions = () => {
        return (
            <div className="crud-actions">
                <Button icon={showFilter ?<IconEyeOpened/>:<IconEyeClosed/>} onClick={handleToggleFilter}>
                    {showFilter ? "显示筛选" : "隐藏筛选"}
                </Button>
                {props.treeTable &&
                    (<Button icon={showExpandRow ? <IconSidebar />:<IconChevronDown />} onClick={()=>setShowExpandRow(!showExpandRow)}>
                    {showExpandRow ? "收起全部" : "展开全部"}
                    </Button>)
                }
                
                {crudActions.map((a, i) => {
                    return (
                        <Button
                            loading={a.loading}
                            key={a.name + i}
                            onClick={() => a.action(a, selectedRows)}
                            type={convertButtonType(a.type)}
                            icon={a.icon}
                            theme="outline"
                        >
                            {a.name}
                        </Button>
                    );
                })}
            </div>
        );
    };

    /**
     * 根据 ColType 生成对应的表单输入控件
     * 筛选面板(filter=true)和编辑弹窗(filter=false)共用此函数
     *
     * 各类型的表单控件:
     *   SELECT/MULTI_SELECT → Form.Select (选项来自 getOptions)
     *   CHECK_BOX           → Checkbox
     *   TAG                 → TagInput (可输入标签)
     *   TREE                → TreeSelect (树形单选)
     *   TREE_MUL            → TreeSelect multiple (树形多选)
     *   DATETIME            → DatePicker type=dateTime
     *   DATE                → DatePicker type=date
     *   NUMBER/AMOUNT/PERCENTAGE → InputNumber (AMOUNT/PERCENTAGE 初始值/100)
     *   IMAGE               → Form.Upload (图片上传)
     *   TEXT                → TextArea
     *   RICH_TEXT           → ReactQuill 富文本编辑器
     *   DATE_RANGE          → DatePicker type=dateRange (仅用于筛选)
     *   STRING(默认)         → Form.Input
     */
    let toFormInput = (col: Col, filter: boolean) => {
        // 筛选模式下不校验必填，编辑模式下根据 optional 决定
        let required = !col.optional;
        let rules = filter ? [] : [{required: required}];
        let initValue = null;
        if (filter) {
            // 筛选表单的初始值来自 initFilterValues
            initValue = props.initFilterValues
                ? getObjValue(props.initFilterValues, col.key)
                : null;
        } else {
            // 编辑表单的初始值来自正在编辑的数据对象
            initValue = editingDto ? getObjValue(editingDto, col.key) : null;
        }
        // 支持动态列类型(如某列在不同行数据下显示不同类型的控件)
        let colType = col.getType ? col.getType(editingDto) : col.type;

        if (colType == ColType.MULTI_SELECT || colType == ColType.SELECT) {
            return (
                <Select
                    showClear
                    filter={true}
                    rules={rules}
                    key={col.key}
                    initValue={initValue}
                    multiple={col.type == ColType.MULTI_SELECT}
                    style={{width: filter?120:200, minWidth: 120}}
                    label={col.name}
                    placeholder={col.placeHolder}
                    helpText={col.helpText}
                    field={col.key}
                    onChange={col.onChange}
                    optionList={
                        options[col.key]
                            ? options[col.key].map((o) => {
                                return {
                                    label: o.label,
                                    value: o.value,
                                    disabled: o.disabled,
                                };
                            })
                            : []
                    }
                />
            );
        } else if (colType == ColType.CHECK_BOX) {
            return (
                <Checkbox 
                style={{width: filter?120:200, minWidth: 120}}
                key={col.key} onChange={col.onChange}>
                    {col.name}
                </Checkbox>
            );
        } else if (colType == ColType.TAG) {
            return (
                <TagInputField
                    rules={rules}
                    allowDuplicates={false}
                    draggable={true}
                    key={col.key}
                    initValue={initValue as string[]}
                    style={{width: filter?120:200, minWidth: 120}}
                    label={col.name}
                    placeholder={col.placeHolder}
                    helpText={col.helpText}
                    field={col.key}
                />
            );
        } else if (colType == ColType.TREE) {
            return (
                <TreeSelectInput
                    showClear={true}
                    rules={rules}
                    key={col.key}
                    treeData={options[col.key]}
                    initValue={initValue}
                    style={{width: filter?120:200, minWidth: 120}}
                    label={col.name}
                    placeholder={col.placeHolder}
                    helpText={col.helpText}
                    field={col.key}
                />
            );
        } else if (colType == ColType.TREE_MUL) {
            return (
                <TreeSelectInput
                    rules={rules}
                    key={col.key}
                    treeData={options[col.key]}
                    initValue={initValue}
                    multiple
                    checkRelation="unRelated"
                    filterTreeNode
                    style={{width: "100%", minWidth: 120}}
                    label={col.name}
                    placeholder={col.placeHolder}
                    helpText={col.helpText}
                    field={col.key}
                />
            );
        } else if (colType == ColType.DATETIME) {
            return (
                <Form.DatePicker
                    style={{width: filter?120:"100%", minWidth: 120}}
                    rules={rules}
                    type="dateTime"
                    key={col.key}
                    initValue={initValue as string[]}
                    label={col.name}
                    placeholder={col.placeHolder}
                    helpText={col.helpText}
                    field={col.key}
                />
            );
        } else if (colType == ColType.DATE) {
            return (
                <Form.DatePicker
                    style={{width: filter?120:"100%", minWidth: 120}}
                    rules={rules}
                    type="date"
                    format={"yyy-MM-dd"}
                    key={col.key}
                    initValue={initValue as string[]}
                    label={col.name}
                    placeholder={col.placeHolder}
                    helpText={col.helpText}
                    field={col.key}
                />
            );
        } else if (
            colType == ColType.NUMBER ||
            colType == ColType.AMOUNT ||
            colType == ColType.PERCENTAGE
        ) {
            // AMOUNT/PERCENTAGE 初始值需要 /100 (后端存的是整数，前端显示浮点数)
            let n = colType == ColType.NUMBER ? initValue : initValue / 100;
            return (
                <Form.InputNumber
                    style={{width: filter?120:200, minWidth: 120}}
                    rules={rules}
                    key={col.key}
                    initValue={n}
                    label={col.name}
                    placeholder={col.placeHolder}
                    helpText={col.helpText}
                    field={col.key}
                />
            );
        } else if (colType == ColType.IMAGE) {
            return (
                <Form.Upload
                    rules={rules}
                    key={col.key}
                    name="file"
                    draggable={true}
                    limit={col.imageLimit || 1}
                    listType="picture"
                    initValue={
                        initValue
                            ? [
                                {
                                    uid: "1",
                                    status: "success",
                                    preview: true,
                                    url: initValue,
                                },
                            ]
                            : []
                    }
                    headers={{
                        "X-Auth-Token": apiClient.getToken()!!,
                    }}
                    field={col.key}
                    label={col.name}
                    action={apiClient.getUploadUrl()}
                >
                    <IconPlus size="extra-large"/>
                </Form.Upload>
            );
        } else if (colType == ColType.TEXT) {
            return (
                <Form.TextArea
                    rules={rules}
                    key={col.key}
                    initValue={initValue}
                    rows={col.textRows || 6}
                    label={col.name}
                    placeholder={col.placeHolder}
                    helpText={col.helpText}
                    field={col.key}
                />
            );
        } else if (colType == ColType.RICH_TEXT) {
            return (
                <RichTextEditor
                    rules={rules}
                    key={col.key}
                    style={{height: 300}}
                    helpText={col.helpText}
                    label={col.name}
                    initValue={initValue}
                    field={col.key}
                />
            );
        } else if (colType == ColType.DATE_RANGE) {
            return (
                <FormDatePicker
                    style={{width: filter?120:200, minWidth: 250}}
                    rules={rules}
                    key={col.key}
                    type="dateRange"
                    helpText={col.helpText}
                    label={col.name}
                    initValue={initValue}
                    field={col.key}
                />
            );
        }else {
            // 默认使用 Input (STRING 类型)
            return (
                <Form.Input
                    rules={rules}
                    key={col.key}
                    initValue={initValue}
                    noLabel={col.hidden}
                    style={{display: col.hidden ? "none" : "", width: filter?120:200}}
                    disabled={col.disabled}
                    label={col.name}
                    placeholder={col.placeHolder}
                    helpText={col.helpText}
                    field={col.key}
                />
            );
        }
    };

    /**
     * 新建/编辑弹窗的保存处理
     *
     * 1. 校验表单
     * 2. 特殊类型字段转换:
     *    IMAGE → 提取上传后的URL
     *    AMOUNT/PERCENTAGE → 值×100 (前端浮点→后端整数)
     *    DATETIME → 格式化为日期时间字符串
     *    DATE → 格式化为日期字符串
     * 3. 判断新建/编辑模式:
     *    editingDto 非null → PUT /{entityKey} (编辑, 附加id)
     *    editingDto 为null → POST /{entityKey} (新建)
     * 4. 成功后关闭弹窗、刷新列表、发布 pubsub 消息(触发菜单计数刷新)
     */
    let handleModalOk = async () => {
        await entityFormApi!!.validate();
        setModalLoading(true);
        let values = entityFormApi!!.getValues();
        let dto: Record<string, any> = {};
        Object.assign(dto, values);

        // 遍历列定义，对特殊类型字段进行值转换
        props.cols.forEach((c) => {
            if (c.type == ColType.IMAGE) {
                // IMAGE: 提取上传响应中的URL
                let value = getObjValue(dto, c.key);
                setObjValue(
                    dto,
                    c.key,
                    value[0] && (value[0].response || value[0].url)
                );
            }
            if (c.type == ColType.AMOUNT || c.type == ColType.PERCENTAGE) {
                // AMOUNT/PERCENTAGE: 前端浮点数 → 后端整数(×100)
                let value = getObjValue(dto, c.key);
                setObjValue(dto, c.key, value * 100);
            }
            if (c.type == ColType.DATETIME) {
                // DATETIME: Date对象 → 格式化字符串
                let value = getObjValue(dto, c.key);
                setObjValue(dto, c.key, formatDateTime(value));
            }
            if (c.type == ColType.DATE) {
                // DATE: Date对象 → 格式化字符串
                let value = getObjValue(dto, c.key);
                setObjValue(dto, c.key, formatDate(value));
            }
        });
        try {
            if (editingDto) {
                // 编辑模式: PUT + 附加id
                dto.id = editingDto.id;
                await apiClient.request("/" + props.entityKey, "PUT", undefined, dto);
            } else {
                // 新建模式: POST
                await apiClient.request("/" + props.entityKey, "POST", undefined, dto);
            }
            setEditingDto(null);
            setModalVisible(false);
            Toast.info("保存成功");
            // 发布消息，通知菜单组件刷新计数
            pubsub.publish("refreshMenuCount", "");
            // 刷新当前页数据
            fetchData(currentPage, currentPageSize);
        } finally {
            setModalLoading(false);
        }
    };

    /**
     * 弹窗表单自定义校验器
     * 遍历所有非id、非optional、非隐藏列，检查是否为空
     * IMAGE类型特殊处理: 检查数组长度是否为1
     */
    let modalValidator = (
        values: Record<string, any>
    ): string | Record<string, any> => {
        let errors: Record<string, any> = {};

        props.cols
            .filter((c) => c.key != "id")
            .filter((c) => !c.optional)
            .filter((c) => {
                // 编辑模式隐藏的字段不校验
                if (editingDto) {
                    return !c.hideInEdit;
                } else {
                    return !c.hideInCreate;
                }
            })
            .forEach((c) => {
                let error = false;
                if (c.type == ColType.IMAGE) {
                    // IMAGE: 检查上传数组是否为空
                    error = values[c.key].length != 1;
                } else {
                    // 其他类型: 检查值是否为null
                    error = getObjValue(values, c.key) == null;
                }
                if (error) {
                    errors[c.key] = "请填写" + c.name;
                }
            });
        return Object.keys(errors).length === 0 ? "" : errors;
    };

    // 弹窗取消: 关闭弹窗、清空编辑对象
    let handleModalCancel = () => {
        setModalVisible(false);
        setEditingDto(null);
    };

    // 表格行样式: 偶数行添加背景色(斑马纹效果)
    let handleRow = (record?: any, index?: number) => {
        if (index! % 2 === 0) {
            return {
                style: {
                    background: "var(--semi-color-fill-0)",
                },
            };
        } else {
            return {};
        }
    };

    /**
     * 渲染新建/编辑弹窗
     * 根据 cols 定义自动生成表单字段:
     *   - 排除 id 字段
     *   - 编辑模式排除 hideInEdit 的字段
     *   - 新建模式排除 hideInCreate 的字段
     */
    let entityModal = () => {
        return (
            <Modal icon={<IconModalStroked size={"extra-large"} style={{color: 'blue'}}/>}
                   onCancel={handleModalCancel}
                   confirmLoading={modalLoading}
                   width={600}
                   visible={modalVisible}
                   title={props.name}
                   okText="保存"
                   onOk={handleModalOk}
            >
                <div className="crud-modal">
                    <Form
                        validateFields={modalValidator}
                        labelPosition="left"
                        labelWidth={130}
                        labelAlign="right"
                        getFormApi={(api) => setEntityFormApi(api)}
                    >
                        {props.cols
                            .filter((c) => {
                                let hide = false;
                                if (editingDto) {
                                    hide = c.hideInEdit == true;
                                } else {
                                    hide = c.hideInCreate == true;
                                }
                                return c.key != "id" && !hide;
                            })
                            .map((col) => toFormInput(col, false))}
                    </Form>
                </div>
            </Modal>
        );
    };

    /**
     * 预加载所有列的下拉选项(异步并发)
     * 遍历所有有 getOptions 的列，并发调用 getOptions 并缓存到 options 状态
     */
    let initOptions = (dto?: any | null | undefined): Promise<any> => {
        return Promise.all(
            props.cols
                .filter((c) => c.getOptions)
                .map((c) => c.getOptions!(dto).then((opts) => (options[c.key] = opts)))
        );
    };

    // 组件初始化: 加载首页数据 + 设置表格高度 + 设置窗口标题
    useEffect(() => {
        const sort = getPropsSortOrder();
        let params = {
            ...sort,
            ...props.initParams,
        };
        fetchData(currentPage, props.pageSize, params);
        window.document.title = props.name;

        // 计算表格高度: 窗口高度 - 顶部区域
        const bodyHeight = document.body.offsetHeight;
        const height = bodyHeight - 150;
        setTableHeight(height);

        // 根据 localStorage 中的 table-mode 设置表格滚动模式
        let tableMode = localStorage.getItem("table-mode");
        if (tableMode == "sticky") {
            setScroll({
                x: "max-content",
                y: height
            } as any);
        } else {
            setScroll({
                x: "max-content",
            } as any);
        }

    }, []);

    // 筛选值变化时重新查询(重置到第一页)
    useEffect(() => {
        if (filterValues) {
            fetchData(1, currentPageSize);
        }
    }, [filterValues]);

    // refreshing 变为 true 时触发数据刷新
    useEffect(() => {
        if (refreshing) {
            fetchData(currentPage, currentPageSize).then(() => {
                setRefreshing(false);
            });
        }
    }, [refreshing]);

    // 初始化行选择配置(当前未启用)
    const initRowSelection = () => {
        setRowSelection({
            onChange: (selectedRowKeys?: any[], selectedRows?: any[]) => {
                if (props.select) {
                    setSelectedRows(selectedRows);
                } else {
                    setSelectedRows([]);
                }
            },
            fixed: true,
            key: Math.random() * 10000 + "",
        });
    };

    /**
     * 格式化筛选值
     * 特殊处理 DATE_RANGE 类型: 将 [Date, Date] 转为 "startDate,endDate" 字符串
     */
    let formatFilterValues = (values: any) => {
        if (!values) {
            return {};
        }
        Object.keys(values).forEach((k) => {
            // 同名 key 可能同时存在"展示列(STRING)"与"筛选专用列(DATE_RANGE)"，
            // 日期格式化必须以 DATE_RANGE 的定义为准，否则会把 Date.toString() 原样提交
            var col = props.cols.filter((c) => c.key == k && c.type == ColType.DATE_RANGE)[0]
                || props.cols.filter((c) => c.key == k)[0];
            if (
                col &&
                values[k] &&
                col.type == ColType.DATE_RANGE &&
                values[k][0] &&
                values[k][1]
            ) {
                values[k] = formatDate(values[k][0]) + "," + formatDate(values[k][1]);
            }
        });
        return values;
    };

    // 筛选提交: 读取筛选表单值 → 触发 filterValues 变化 → useEffect 重新查询
    let handleFilterSubmit = () => {
        setFilterValues(filterFormApi?.getValues());
    };

    // 筛选重置: 清空筛选表单 + 清空筛选值 → 触发重新查询
    let handleFilterReset = () => {
        filterFormApi?.reset();
        setFilterValues({});
    };

    /**
     * 渲染筛选面板
     * 1. 筛选列 = cols 中 showInFilter=true 的列
     * 2. 展开筛选列 = cols 中 showInPlusFilter=true 的列(默认折叠，点击"展开"后显示)
     * 3. 按 colFilterSort 排序
     * 4. showFilter=true 时隐藏筛选面板(按钮文字切换为"显示筛选")
     */
    let filterForm = () => {
        let filterCols = props.cols.filter((c) => c.showInFilter);
        if (filterCols.length == 0) {
            return <div></div>;
        }
        // 按 colFilterSort 排序(有排序值的排在前面)
        filterCols.sort((a, b) => {
            if (a.colFilterSort == undefined && b.colFilterSort == undefined) {
                return 0;
            } else if (b.colFilterSort == undefined) {
                return -1;
            } else if (a.colFilterSort == undefined) {
                return 1;
            } else {
                return a.colFilterSort - b.colFilterSort;
            }
        });

        // 展开/收起的额外筛选列
        let filterPlusCols = props.cols.filter((c) => c.showInPlusFilter);
        if (filterPlusCols.length > 0) {
            filterCols.sort((a, b) => {
                if (a.colFilterSort == undefined && b.colFilterSort == undefined) {
                    return 0;
                } else if (b.colFilterSort == undefined) {
                    return -1;
                } else if (a.colFilterSort == undefined) {
                    return 1;
                } else {
                    return a.colFilterSort - b.colFilterSort;
                }
            });
        }

        // showFilter=true 表示筛选面板已隐藏
        if (showFilter) {
            return <div></div>;
        }

        return (
            <div className="crud-filter-form">
                <Form
                    getFormApi={(api) => {
                        setFilterFormApi(api);
                        if (props.getFilterFormApi) {
                            props.getFilterFormApi(crudApiV, api);
                        }
                    }}
                    layout="horizontal"
                    initValues={props.initFilterValues}
                >
                    {/* 基本筛选列 */}
                    {filterCols.map((col) => toFormInput(col, true))}

                    {/* 展开筛选列 + 展开/收起按钮 */}
                    {filterPlusCols.length > 0 && (
                        <>
                            {filterPlusCols.map((col) => {
                                if (showPlusFilter == "none") {
                                    return toFormInput(col, true);
                                } else {
                                    <></>;
                                }
                            })}

                            <Form.Slot label={"⠀"}>
                                <Button
                                    style={{display: showPlusFilter}}
                                    onClick={() => {
                                        setShowPlusFilter("none");
                                        setShowMinusFilter("");
                                    }}
                                    icon={<IconPlus/>}
                                    htmlType="submit"
                                >
                                    展开
                                </Button>
                            </Form.Slot>
                            <Form.Slot label={"⠀"}>
                                <Button
                                    style={{display: showMinusFilter}}
                                    onClick={() => {
                                        setShowPlusFilter("");
                                        setShowMinusFilter("none");
                                    }}
                                    icon={<IconMinus/>}
                                    htmlType="submit"
                                >
                                    收起
                                </Button>
                            </Form.Slot>
                        </>
                    )}

                    {/* 筛选/重置按钮 */}
                    <Form.Slot label={"⠀"}>
                        <Button icon={<IconSearch/>} theme="solid"
                                onClick={handleFilterSubmit}
                                loading={loading}
                                htmlType="submit"
                        >
                            筛选
                        </Button>
                    </Form.Slot>
                    <Form.Slot label={"⠀"}>
                        <Button icon={<IconUndo/>} type="warning" theme="solid"
                                onClick={handleFilterReset}
                                loading={loading}
                                htmlType="reset"
                        >
                            重置
                        </Button>
                    </Form.Slot>
                </Form>
                <Divider margin="5px"/>
            </div>
        );
    };

    /**
     * Table onChange 处理: 统一处理排序和分页
     * 从事件对象中提取排序条件 → 重新加载数据
     * 特殊处理: pageSize=500 时不分页(始终加载第一页)
     */
    const handleChange = (e: any) => {
        let sort = getSort(e);
        if(currentPageSize  == 500){
            // 不分页模式(如树形数据)
            fetchData(1, currentPageSize, sort);
        }else{
           fetchData(e.pagination.currentPage, e.pagination.pageSize, sort);
        }
    };

    // 从 Table onChange 事件中提取排序条件
    const getSort = (e: any) => {
        if (e.sorter && e.sorter.sortOrder) {
            const result: any = {};
            return {
                orderBy: e.sorter.dataIndex + "," + e.sorter.sortOrder,
            };
        } else {
            return {};
        }
    };

    // 计算表格剩余高度(用于 sticky 模式)
    const getResidueHeightByDOMRect = () => {
        const bodyHeight = document.body.offsetHeight;
        let arr = document.getElementsByClassName("semi-table-wrapper");
        const tableBodyTop = arr[arr.length - 1].getBoundingClientRect().top;
        const paginationHeight = 32 + 16 * 2;
        const tabContentBottomPadding = 20;
        const contentBottomPadding = 20;
        const residueHeight =
            bodyHeight -
            tableBodyTop -
            paginationHeight -
            contentBottomPadding -
            tabContentBottomPadding;
        return residueHeight;
    };

    return (
        <div className="crud-page">
            {/* 新建/编辑弹窗 */}
            {entityModal()}
            {/* 页面标题(可选) */}
            {props.hideTitle == true || props.hideTitle == undefined ? null : (
                <div className="crud-title">
                    <Title heading={5}>{props.name}</Title>
                </div>
            )}
            <Divider margin="5px"/>
            <div className="crud-content">
                {/* 筛选面板(可折叠) */}
                {filterForm()}
                {/* 顶部操作栏(筛选切换 + 新建/导出/自定义按钮) */}
                {actions()}
                {/* 自定义描述区域 */}
                {props.desc}
                {/* 数据表格 */}
                <Table
                    scroll={scroll}
                    // 行选择(仅 select=true 时启用)
                    rowSelection={props.select ? {
                        selectedRowKeys,
                        onChange: (selectedRowKeys?: any[], selectedRows?: any[]) => {
                            if (props.select) {
                                setSelectedRows(selectedRows);
                                setSelectedRowKeys(selectedRowKeys as string[]);
                            } else {
                                setSelectedRows([]);
                            }
                        },
                        fixed: true,
                    } : false}
                    size="small"
                    // 行唯一标识(默认 "id"，可通过 rowKey 自定义)
                    rowKey={(r) => (props.rowKey ? r[props.rowKey] : r.id)}
                    // 斑马纹
                    onRow={handleRow}
                    expandAllRows={showExpandRow}
                    loading={loading}
                    columns={columns}
                    bordered={props.bordered}
                    dataSource={page.records}
                    // 分页配置: pageSize=500 时不显示分页器
                    pagination={page.size==500?false:{
                        currentPage: page.current,
                        pageSize: page.size,
                        total: page.total,
                        showSizeChanger: true,
                        pageSizeOpts: [20, 30, 50, 100, 200],
                        onPageChange: handlePageChange,
                    }}
                    // 统一处理排序和分页变化
                    onChange={handleChange}
                />
            </div>
        </div>
    );
}
